/**
 * Invoice utility functions for accounting splits
 * Note: VAT is already included in the ticket price
 */

import type { Firestore } from 'firebase/firestore';
import { runTransaction, doc, getDoc } from 'firebase/firestore';
import type { Invoice } from '@/types';

const AMOUNT_EPS = 0.01;

/** Tolerance for comparing settled ticket amounts (exported for dashboard RSVP rows). */
export const INVOICE_AMOUNT_EPS = AMOUNT_EPS;

/**
 * Amount the payer has marked as transferred and is awaiting treasurer approval.
 * Only applies while the invoice is in `pending_verification`.
 */
export function getPendingVerificationAmount(invoice: Invoice): number {
  if (invoice.status !== 'pending_verification') {
    return 0;
  }
  const pending = Number(invoice.pendingVerificationAmount);
  return Number.isFinite(pending) && pending > AMOUNT_EPS ? pending : 0;
}

/** Confirmed payments plus bank transfers awaiting approval. */
export function getAmountReservedOnInvoice(invoice: Invoice): number {
  return getEffectiveAmountPaid(invoice) + getPendingVerificationAmount(invoice);
}

/**
 * Firestore sometimes has total/outstanding in sync while amountPaid is missing.
 * Derive money collected from declared amountPaid, total − outstanding, or status.
 */
export function getEffectiveAmountPaid(invoice: Invoice): number {
  const total = Number(invoice.totalAmount) || 0;
  const outstanding = Math.max(0, Number(invoice.outstandingBalance) || 0);
  const declared = Number(invoice.amountPaid) || 0;
  const implied = Math.max(0, Math.round((total - outstanding) * 100) / 100);

  if (declared > AMOUNT_EPS) {
    return declared;
  }
  if (implied > AMOUNT_EPS) {
    return implied;
  }
  if (invoice.status === 'paid' && total > AMOUNT_EPS) {
    return total;
  }
  if (invoice.status === 'partial' && total > AMOUNT_EPS) {
    return implied;
  }
  return 0;
}

/**
 * Ticket portion still owed (headline figure), aligned with My RSVP dashboard:
 * live ticket subtotal minus effective amount collected. Excludes the bank/transaction fee
 * from the displayed balance when the invoice splits subtotal vs fee (same as
 * displayTotal − getEffectiveAmountPaid on My RSVP).
 */
export function getInvoiceTicketAmountDue(invoice: Invoice): number {
  if (invoice.status === 'voided') {
    return 0;
  }
  const paid = getAmountReservedOnInvoice(invoice);
  const rawSubtotal = Number(invoice.subtotal);
  let ticketPot: number;
  if (Number.isFinite(rawSubtotal) && rawSubtotal >= 0) {
    ticketPot = rawSubtotal;
  } else {
    const fee = Number(invoice.transactionFee) || 0;
    const total = Number(invoice.totalAmount) || 0;
    ticketPot = fee > 0 ? Math.max(0, total - fee) : total;
  }
  return Math.max(0, Math.round((ticketPot - paid) * 100) / 100);
}

export interface InvoiceCalculationResult {
  subtotal: number;
  vatAmount: number;
  transactionFee: number;
  totalAmount: number;
  accountingSplit: {
    lodgeTransfer: number;
    platformProfit: number;
  };
}

/**
 * Calculate invoice amounts
 * VAT is already included in the ticket price, so we only add transaction fee
 * @param baseAmount - The ticket price (VAT inclusive, cost per person * number of guests)
 * @param transactionFee - Transaction fee if applicable (0 if not set in admin)
 * @returns Invoice calculation result with all breakdowns
 */
export function calculateInvoiceAmounts(
  baseAmount: number,
  transactionFee: number = 0
): InvoiceCalculationResult {
  // Subtotal is the base amount (VAT is already included in this price)
  const subtotal = baseAmount;

  // VAT is already included in the ticket price, so vatAmount is 0
  const vatAmount = 0;

  // Total amount is subtotal + transaction fee only
  const totalAmount = subtotal + transactionFee;

  // Accounting split:
  // - Lodge transfer: subtotal (the full ticket price goes to the lodge, VAT already included)
  // - Platform profit: transaction fee only (platform keeps the transaction fee)
  const accountingSplit = {
    lodgeTransfer: subtotal,
    platformProfit: transactionFee,
  };

  return {
    subtotal,
    vatAmount,
    transactionFee,
    totalAmount,
    accountingSplit,
  };
}

/**
 * Generate a unique invoice number (legacy fallback)
 * Format: XXXXXX (6 random alphanumeric characters)
 */
export function generateInvoiceNumber(): string {
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return random;
}

function isPermissionDeniedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const firestoreError = error as { code?: string };
  return firestoreError.code === 'permission-denied';
}

/**
 * Generate a unit-based invoice reference for bank transfer
 * Format: UNITNUMBER-INVOICENUMBER (e.g. 5461-45)
 * Uses a Firestore transaction to atomically increment the unit's invoice counter
 * @param db - Firestore instance
 * @param unitId - Unit document ID
 * @returns Promise resolving to the invoice reference string
 */
export async function generateUnitInvoiceNumber(db: Firestore, unitId: string): Promise<string> {
  const unitRef = doc(db, 'units', unitId);

  try {
    return await runTransaction(db, async (transaction) => {
      const unitSnap = await transaction.get(unitRef);

      if (!unitSnap.exists()) {
        return generateInvoiceNumber();
      }

      const unitData = unitSnap.data();
      const unitNumber = unitData?.unitNumber || unitData?.number || '';

      if (!unitNumber) {
        return generateInvoiceNumber();
      }

      const currentCounter = unitData?.invoiceCounter ?? 0;
      const newCounter = currentCounter + 1;

      transaction.update(unitRef, { invoiceCounter: newCounter });
      return `${unitNumber}-${newCounter}`;
    });
  } catch (error) {
    if (!isPermissionDeniedError(error)) {
      throw error;
    }

    const unitSnap = await getDoc(unitRef);
    const unitData = unitSnap.exists() ? unitSnap.data() : null;
    const unitNumber = unitData?.unitNumber || unitData?.number || '';

    if (!unitNumber) {
      return generateInvoiceNumber();
    }

    const fallbackSuffix = generateInvoiceNumber();
    return `${unitNumber}-${fallbackSuffix}`;
  }
}

/**
 * Calculate payment split for a partial payment
 * @param paymentAmount - The amount being paid
 * @param totalAmount - Total invoice amount
 * @param accountingSplit - The accounting split for the total invoice
 * @returns Proportional accounting split for this payment
 */
export function calculatePaymentSplit(
  paymentAmount: number,
  totalAmount: number,
  accountingSplit: { lodgeTransfer: number; platformProfit: number }
): { lodgeTransfer: number; platformProfit: number } {
  if (totalAmount === 0) {
    return { lodgeTransfer: 0, platformProfit: 0 };
  }

  // Calculate proportional split
  const proportion = paymentAmount / totalAmount;
  return {
    lodgeTransfer: accountingSplit.lodgeTransfer * proportion,
    platformProfit: accountingSplit.platformProfit * proportion,
  };
}

